import { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Bell, Search, X, Users, Mail, Smartphone,
  Send, CheckCircle, Clock, Eye, EyeOff,
} from 'lucide-react';

export default function AdminNotifications() {
  // ── Compose state ─────────────────────────────────────────
  const [title,   setTitle]   = useState('');
  const [message, setMessage] = useState('');
  const [target,  setTarget]  = useState('all');   // 'all' | 'selected'
  const [via,     setVia]     = useState('in_app'); // 'in_app' | 'email' | 'both'
  const [sending, setSending] = useState(false);

  // ── User search/select state ──────────────────────────────
  const [query,    setQuery]    = useState('');
  const [results,  setResults]  = useState([]);
  const [selected, setSelected] = useState([]); // [{_id, name, username, email, avatar}]
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);

  // ── History state ─────────────────────────────────────────
  const [history,  setHistory]  = useState([]);
  const [histLoad, setHistLoad] = useState(true);
  const [tab,      setTab]      = useState('compose'); // 'compose' | 'history'

  // ── Fetch history ─────────────────────────────────────────
  useEffect(() => {
    api.get('/admin/notifications/history')
      .then(({ data }) => setHistory(data.history || []))
      .catch(() => {})
      .finally(() => setHistLoad(false));
  }, [tab]);

  // ── Debounced user search ─────────────────────────────────
  useEffect(() => {
    if (query.length < 1) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get('/admin/notifications/search-users?q=' + query);
        // Exclude already-selected users
        setResults(data.users.filter((u) => !selected.find((s) => s._id === u._id)));
      } catch {}
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query, selected]);

  const addUser = (u) => {
    setSelected((prev) => prev.find((s) => s._id === u._id) ? prev : [...prev, u]);
    setQuery('');
    setResults([]);
  };

  const removeUser = (id) => setSelected((prev) => prev.filter((u) => u._id !== id));

  // ── Send ──────────────────────────────────────────────────
  const handleSend = async () => {
    if (!title.trim())   return toast.error('Title is required.');
    if (!message.trim()) return toast.error('Message is required.');
    if (target === 'selected' && selected.length === 0)
      return toast.error('Select at least one user.');

    setSending(true);
    try {
      const { data } = await api.post('/admin/notifications/send', {
        title,
        message,
        target,
        via,
        userIds: selected.map((u) => u._id),
      });
      toast.success(data.message);
      setTitle('');
      setMessage('');
      setSelected([]);
      setTarget('all');
      // Refresh history
      const h = await api.get('/admin/notifications/history');
      setHistory(h.data.history || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to send.');
    } finally {
      setSending(false);
    }
  };

  const viaLabel = { in_app: 'In-App Only', email: 'Email Only', both: 'In-App + Email' };
  const viaIcon  = { in_app: Smartphone, email: Mail, both: Bell };

  return (
    <div className="fade-in" style={{ maxWidth: 820, margin: '0 auto' }}>

      {/* Page header */}
      <div className="admin-page-header">
        <div>
          <h2>Notifications</h2>
          <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 4 }}>
            Send in-app or email notifications to users
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 'var(--space-5)',
                    borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        {[
          { key: 'compose', label: 'Compose', icon: Send },
          { key: 'history', label: 'History',  icon: Clock },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '10px 18px',
              border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '0.875rem', fontWeight: tab === key ? 700 : 400,
              color: tab === key ? 'var(--accent)' : 'var(--text-muted)',
              borderBottom: tab === key ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom: -1, transition: 'all 0.15s',
            }}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {/* ── COMPOSE TAB ────────────────────────────────────── */}
      {tab === 'compose' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>

          {/* Delivery method */}
          <div className="card" style={{ padding: 'var(--space-5)' }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)',
                            textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 12 }}>
              Delivery Method
            </label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                { key: 'in_app', label: 'In-App Only',      icon: Smartphone, desc: 'Bell notification in the website' },
                { key: 'email',  label: 'Email Only',        icon: Mail,       desc: 'Send to registered email address' },
                { key: 'both',   label: 'In-App + Email',    icon: Bell,       desc: 'Both channels simultaneously' },
              ].map(({ key, label, icon: Icon, desc }) => (
                <button key={key} onClick={() => setVia(key)}
                  style={{
                    flex: '1 1 180px',
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                    gap: 6, padding: '14px 16px',
                    border: `2px solid ${via === key ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius)',
                    background: via === key ? 'var(--accent-soft)' : 'var(--bg)',
                    cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Icon size={16} color={via === key ? 'var(--accent)' : 'var(--text-muted)'} />
                    <span style={{ fontWeight: 600, fontSize: '0.85rem',
                                   color: via === key ? 'var(--accent)' : 'var(--text)' }}>
                      {label}
                    </span>
                  </div>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                    {desc}
                  </span>
                </button>
              ))}
            </div>
            {(via === 'email' || via === 'both') && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--warning-soft)',
                            border: '1px solid #fde68a', borderRadius: 'var(--radius)',
                            fontSize: '0.78rem', color: 'var(--warning)' }}>
                ⚠️ Email requires SMTP configured in server <code>.env</code> (SMTP_HOST, SMTP_USER, SMTP_PASS)
              </div>
            )}
          </div>

          {/* Recipients */}
          <div className="card" style={{ padding: 'var(--space-5)' }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)',
                            textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 12 }}>
              Recipients
            </label>

            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              {[
                { key: 'all',      label: '🌐  All Users',      desc: 'Everyone with a complete profile' },
                { key: 'selected', label: '🎯  Selected Users',  desc: 'Pick specific users' },
              ].map(({ key, label, desc }) => (
                <button key={key} onClick={() => setTarget(key)}
                  style={{
                    flex: 1, padding: '12px 16px',
                    border: `2px solid ${target === key ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius)',
                    background: target === key ? 'var(--accent-soft)' : 'var(--bg)',
                    cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                  }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem',
                                color: target === key ? 'var(--accent)' : 'var(--text)' }}>
                    {label}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                    {desc}
                  </div>
                </button>
              ))}
            </div>

            {/* User search — only when 'selected' */}
            {target === 'selected' && (
              <div>
                {/* Selected chips */}
                {selected.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                    {selected.map((u) => (
                      <div key={u._id}
                        style={{ display: 'flex', alignItems: 'center', gap: 6,
                                 padding: '4px 10px 4px 8px',
                                 background: 'var(--accent-soft)',
                                 border: '1px solid var(--accent)',
                                 borderRadius: 999, fontSize: '0.78rem', color: 'var(--accent)' }}>
                        <div style={{ width: 20, height: 20, borderRadius: '50%',
                                      background: 'var(--accent)', color: '#fff',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontSize: '0.6rem', fontWeight: 700, flexShrink: 0 }}>
                          {u.name?.[0]?.toUpperCase()}
                        </div>
                        <span style={{ fontWeight: 600 }}>{u.name}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                          @{u.username}
                        </span>
                        <button onClick={() => removeUser(u._id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer',
                                   color: 'var(--accent)', display: 'flex', padding: 0, marginLeft: 2 }}>
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Search input */}
                <div style={{ position: 'relative' }} ref={searchRef}>
                  <div style={{ position: 'relative' }}>
                    <Search size={14} style={{ position: 'absolute', left: 12, top: '50%',
                                               transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search by name, @username, roll number or email..."
                      style={{ width: '100%', padding: '10px 12px 10px 36px',
                               border: '1px solid var(--border-dark)', borderRadius: 'var(--radius)',
                               fontSize: '0.85rem', background: 'var(--bg)', color: 'var(--text)',
                               outline: 'none', boxSizing: 'border-box' }}
                    />
                    {searching && (
                      <div className="spinner" style={{ position: 'absolute', right: 12, top: '50%',
                                                        transform: 'translateY(-50%)', width: 16, height: 16 }} />
                    )}
                  </div>

                  {/* Dropdown results */}
                  {results.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                                  background: 'var(--surface)', border: '1px solid var(--border)',
                                  borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-md)',
                                  maxHeight: 260, overflowY: 'auto', marginTop: 4 }}>
                      {results.map((u) => (
                        <button key={u._id} onClick={() => addUser(u)}
                          style={{ display: 'flex', alignItems: 'center', gap: 10,
                                   width: '100%', padding: '10px 14px',
                                   border: 'none', background: 'none', cursor: 'pointer',
                                   textAlign: 'left', borderBottom: '1px solid var(--border)',
                                   transition: 'background 0.1s' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          {u.avatar
                            ? <img src={u.avatar} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                            : <div style={{ width: 32, height: 32, borderRadius: '50%',
                                           background: 'var(--accent-soft)', color: 'var(--accent)',
                                           display: 'flex', alignItems: 'center', justifyContent: 'center',
                                           fontWeight: 700, fontSize: '0.85rem', flexShrink: 0 }}>
                                {u.name?.[0]?.toUpperCase()}
                              </div>
                          }
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text)' }}>
                              {u.name}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                              @{u.username} · {u.rollNumber} · {u.email}
                            </div>
                          </div>
                          <span style={{ fontSize: '0.72rem', color: 'var(--accent)',
                                         background: 'var(--accent-soft)', padding: '2px 8px',
                                         borderRadius: 999, flexShrink: 0 }}>
                            + Add
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {selected.length > 0 && (
                  <div style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {selected.length} user{selected.length !== 1 ? 's' : ''} selected
                    <button onClick={() => setSelected([])}
                      style={{ marginLeft: 10, background: 'none', border: 'none',
                               cursor: 'pointer', color: 'var(--danger)', fontSize: '0.78rem' }}>
                      Clear all
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Message content */}
          <div className="card" style={{ padding: 'var(--space-5)' }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)',
                            textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 12 }}>
              Notification Content
            </label>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text)',
                                display: 'block', marginBottom: 6 }}>
                  Title *
                </label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. New Feature Available!"
                  maxLength={80}
                  style={{ width: '100%', padding: '10px 14px',
                           border: '1px solid var(--border-dark)', borderRadius: 'var(--radius)',
                           fontSize: '0.875rem', background: 'var(--bg)', color: 'var(--text)',
                           outline: 'none', boxSizing: 'border-box' }}
                />
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4, textAlign: 'right' }}>
                  {title.length}/80
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text)',
                                display: 'block', marginBottom: 6 }}>
                  Message *
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Write your notification message here..."
                  maxLength={500}
                  rows={4}
                  style={{ width: '100%', padding: '10px 14px',
                           border: '1px solid var(--border-dark)', borderRadius: 'var(--radius)',
                           fontSize: '0.875rem', background: 'var(--bg)', color: 'var(--text)',
                           outline: 'none', resize: 'vertical', boxSizing: 'border-box',
                           fontFamily: 'var(--font-body)' }}
                />
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4, textAlign: 'right' }}>
                  {message.length}/500
                </div>
              </div>
            </div>
          </div>

          {/* Preview + Send */}
          {(title || message) && (
            <div className="card" style={{ padding: 'var(--space-5)', background: 'var(--bg)' }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)',
                              textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 12 }}>
                Preview
              </label>
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                            borderRadius: 'var(--radius)', padding: '14px 16px',
                            borderLeft: '4px solid var(--accent)' }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text)', marginBottom: 4 }}>
                  {title || '(no title)'}
                </div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  {message || '(no message)'}
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-light)', marginTop: 8,
                              display: 'flex', alignItems: 'center', gap: 6 }}>
                  {via === 'in_app' && <><Smartphone size={11} /> In-App</>}
                  {via === 'email'  && <><Mail size={11} /> Email</>}
                  {via === 'both'   && <><Bell size={11} /> In-App + Email</>}
                  <span>·</span>
                  <Users size={11} />
                  {target === 'all' ? 'All users' : `${selected.length} selected`}
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleSend}
            disabled={sending || !title.trim() || !message.trim() || (target === 'selected' && selected.length === 0)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                     padding: '14px 28px', background: 'var(--accent)', color: '#fff',
                     border: 'none', borderRadius: 'var(--radius)', fontWeight: 700,
                     fontSize: '0.9rem', cursor: 'pointer', transition: 'opacity 0.15s',
                     opacity: (sending || !title.trim() || !message.trim() || (target === 'selected' && selected.length === 0)) ? 0.5 : 1 }}>
            {sending
              ? <><div className="spinner" style={{ width: 16, height: 16, borderColor: '#fff3', borderTopColor: '#fff' }} /> Sending...</>
              : <><Send size={16} /> Send Notification</>
            }
          </button>
        </div>
      )}

      {/* ── HISTORY TAB ────────────────────────────────────── */}
      {tab === 'history' && (
        <div>
          {histLoad ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
              <div className="spinner spinner-lg" />
            </div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
              <Bell size={40} strokeWidth={1.5} style={{ marginBottom: 12, opacity: 0.4 }} />
              <p style={{ fontWeight: 600, margin: 0 }}>No notifications sent yet</p>
              <span style={{ fontSize: '0.82rem' }}>Use the Compose tab to send your first notification</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {history.map((h, i) => {
                const ViaIcon = { in_app: Smartphone, email: Mail, both: Bell }[h.sentVia] || Bell;
                const readPct = h.count > 0 ? Math.round((h.readCount / h.count) * 100) : 0;
                return (
                  <div key={i} className="card" style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                      <div style={{ width: 38, height: 38, borderRadius: 8, flexShrink: 0,
                                    background: 'var(--accent-soft)', color: 'var(--accent)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ViaIcon size={17} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text)' }}>
                            {h.title}
                          </span>
                          <span style={{ fontSize: '0.7rem', background: 'var(--bg-2)',
                                         border: '1px solid var(--border)', borderRadius: 999,
                                         padding: '2px 8px', color: 'var(--text-muted)' }}>
                            {h.sentVia === 'in_app' ? 'In-App' : h.sentVia === 'email' ? 'Email' : 'In-App + Email'}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>
                          {h.message}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.72rem', color: 'var(--text-light)',
                                         display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Users size={11} /> {h.count} recipient{h.count !== 1 ? 's' : ''}
                          </span>
                          {h.sentVia !== 'email' && (
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-light)',
                                           display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Eye size={11} /> {h.readCount} read ({readPct}%)
                            </span>
                          )}
                          <span style={{ fontSize: '0.72rem', color: 'var(--text-light)',
                                         display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Clock size={11} />
                            {new Date(h.createdAt).toLocaleString('en-IN', {
                              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}